import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth, hasRole } from "../lib/auth";
import { PageHeader } from "../components/Primitives";
import Pagination, { usePagination } from "../components/Pagination";
import { useApiLoading, LoadingRow } from "../components/TableLoader";
import { ChevronLeft, FileDown, Play, Loader2, CheckCircle2, AlertTriangle, History, X as XIcon, RotateCw, Clock, Columns3, Check, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { downloadCsv } from "../lib/csv";
import { fmtMoney } from "../lib/money";
import { parseUtc } from "../lib/datetime";

const BREAKDOWNS = [
    { value: "creative", label: "Creative (no breakdown)" },
    { value: "placement", label: "Placement & Platform" },
    { value: "age", label: "Age" },
    { value: "gender", label: "Gender" },
    { value: "region", label: "Region" },
    { value: "device", label: "Device" },
];

// Every metric available in `meta_insights`. Order = column order.
const ALL_METRICS = [
    { key: "spend", label: "Spend", format: "money" },
    { key: "client_cpm", label: "Client CPM", format: "money", internal: true },
    { key: "display_spend", label: "Campaign Budget", format: "money", internal: true },
    { key: "margin", label: "Margin", format: "money", internal: true },
    { key: "margin_pct", label: "Margin %", format: "percent", internal: true },
    { key: "reach", label: "Reach" },
    { key: "impressions", label: "Impressions" },
    { key: "clicks", label: "Clicks" },
    { key: "link_clicks", label: "Link clicks" },
    { key: "page_engagement", label: "Page engagement" },
    { key: "post_engagement", label: "Post engagement" },
    { key: "post_comments", label: "Post comments" },
    { key: "post_shares", label: "Post shares" },
    { key: "post_saves", label: "Post saves" },
    { key: "post_reactions", label: "Post reactions" },
    { key: "landing_page_views", label: "Landing page views" },
    { key: "video_views", label: "Video views" },
    { key: "three_sec_video_plays", label: "3-sec video plays" },
    { key: "video_thruplay", label: "ThruPlay" },
    { key: "video_p25", label: "Video 25%" },
    { key: "video_p50", label: "Video 50%" },
    { key: "video_p75", label: "Video 75%" },
    { key: "video_p95", label: "Video 95%" },
    { key: "video_p100", label: "Video 100%" },
    { key: "video_2s", label: "2-sec video views" },
    { key: "video_6s", label: "6-sec video views" },
    { key: "frequency", label: "Frequency", format: "decimal" },
    { key: "ctr_all", label: "CTR (all)", format: "percent" },
    { key: "ctr_link", label: "CTR (link click)", format: "percent" },
    { key: "fb_likes", label: "Facebook likes" },
    { key: "app_activations", label: "App activations" },
    { key: "currency", label: "Currency", format: "text" },
    { key: "reporting_start", label: "Reporting starts", format: "text" },
    { key: "reporting_end", label: "Reporting ends", format: "text" },
];

const NON_SUMMABLE = new Set(["margin_pct", "client_cpm", "frequency", "ctr_all", "ctr_link", "currency", "reporting_start", "reporting_end"]);

const DEFAULT_METRICS = ["spend", "display_spend", "margin", "margin_pct", "impressions", "clicks", "link_clicks", "video_views", "video_thruplay"];
const LS_KEY = "meta_detailed_metrics_v3";

const fmtNum = (v) => (v || 0).toLocaleString("en-US");
const fmtPct = (v) => `${(Number(v) || 0).toFixed(2)}%`;

const renderMetric = (row, metric) => {
    const val = row[metric.key];
    if (metric.format === "money") return fmtMoney(val, row.currency);
    if (metric.format === "percent") return fmtPct(val);
    if (metric.format === "decimal") return (Number(val) || 0).toFixed(2);
    if (metric.format === "text") return val || "—";
    return fmtNum(val);
};

export default function MetaDetailedReports() {
    const { user } = useAuth();
    const apiLoading = useApiLoading();

    const [adAccounts, setAdAccounts] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const today = new Date().toISOString().slice(0, 10);
    const monthAgo = new Date(Date.now() - 29 * 86400000).toISOString().slice(0, 10);
    const [filter, setFilter] = useState({
        ad_account_id: "",
        campaign_external_id: "",
        breakdown: "placement",
        date_from: monthAgo,
        date_to: today,
    });
    const [rows, setRows] = useState([]);
    const [generating, setGenerating] = useState(false);
    const [hasGenerated, setHasGenerated] = useState(false);
    const [sweepJobs, setSweepJobs] = useState([]);
    const [connections, setConnections] = useState([]);
    const [metrics, setMetrics] = useState(() => {
        try {
            const stored = JSON.parse(localStorage.getItem(LS_KEY) || "null");
            if (Array.isArray(stored) && stored.length) return stored;
        } catch (e) { void e; }
        return DEFAULT_METRICS;
    });
    const [metricMenuOpen, setMetricMenuOpen] = useState(false);
    const [allowedKeys, setAllowedKeys] = useState(null); // null = loading; array = restricted set

    // Load this user's agency's allowed-metrics list (super admin gets full catalog)
    useEffect(() => {
        api.get("/detailed-reports/meta/allowed-metrics")
            .then((r) => setAllowedKeys(r.data.allowed_metrics))
            .catch(() => setAllowedKeys(ALL_METRICS.map((m) => m.key)));
    }, []);

    // The metrics catalog visible to this user (filtered to allowed). All access levels see the
    // unified "Campaign Budget" label for display_spend.
    const isAdvertiser = user?.role === "advertiser";
    const allowedMetrics = allowedKeys ? ALL_METRICS.filter((m) => allowedKeys.includes(m.key)) : ALL_METRICS;

    // Drop any saved selection entries that are no longer permitted
    useEffect(() => {
        if (!allowedKeys) return;
        setMetrics((prev) => prev.filter((k) => allowedKeys.includes(k)));
    }, [allowedKeys]); // eslint-disable-line

    // Persist metric selection
    useEffect(() => {
        try { localStorage.setItem(LS_KEY, JSON.stringify(metrics)); } catch (e) { void e; }
    }, [metrics]);

    const toggleMetric = (key) => {
        setMetrics((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
    };
    const allSelected = allowedMetrics.length > 0 && metrics.length === allowedMetrics.length;
    const selectAll = () => setMetrics(allowedMetrics.map((m) => m.key));
    const clearAll = () => setMetrics([]);
    const visibleMetrics = allowedMetrics.filter((m) => metrics.includes(m.key));

    // Load Meta ad accounts + Meta connections
    useEffect(() => {
        api.get("/ad-accounts").then((r) => {
            const meta = r.data.filter((a) => a.platform === "meta");
            setAdAccounts(meta);
            if (meta.length && !filter.ad_account_id) {
                setFilter((f) => ({ ...f, ad_account_id: meta[0].id }));
            }
        });
        api.get("/platform-connections").then((r) => setConnections(r.data.filter((c) => c.platform === "meta")));
    }, []); // eslint-disable-line

    const selectedAcc = adAccounts.find((a) => a.id === filter.ad_account_id);
    const selectedConn = selectedAcc ? connections.find((c) => c.id === selectedAcc.platform_connection_id) : null;

    // Poll sweep jobs for the selected connection
    useEffect(() => {
        if (!selectedConn) return;
        const tick = async () => {
            try {
                const r = await api.get(`/detailed-reports/meta/sweep-jobs`, { params: { connection_id: selectedConn.id } });
                setSweepJobs(r.data);
            } catch (e) { void e; }
        };
        tick();
        const id = setInterval(tick, 4000);
        return () => clearInterval(id);
    }, [selectedConn?.id]); // eslint-disable-line

    const activeSweep = sweepJobs.find((j) => j.status === "queued" || j.status === "running");
    const lastSweep = sweepJobs[0];
    // Hide the full progress card on a clean success — show a compact "last sweep" label instead.
    const sweepSucceeded = !activeSweep && lastSweep && lastSweep.status === "success";
    const showSweepCard = !!activeSweep || (!!lastSweep && lastSweep.status !== "queued" && !sweepSucceeded);

    const startManualSweep = async () => {
        if (!selectedConn) return toast.error("Select an ad account first");
        try {
            await api.post("/detailed-reports/meta/sweep-jobs/start", { connection_id: selectedConn.id });
            toast.success("Sweep queued — fetching every campaign's data from its start date");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };

    const cancelSweep = async (jobId) => {
        if (!window.confirm("Cancel this sweep? In-flight campaigns will finish then the job stops.")) return;
        try {
            await api.post(`/detailed-reports/meta/sweep-jobs/${jobId}/cancel`);
            toast.success("Cancellation requested");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };

    const restartSweep = async (jobId) => {
        if (!window.confirm("Restart this sweep from campaign 1? Existing rows will be refreshed (upsert).")) return;
        try {
            await api.post(`/detailed-reports/meta/sweep-jobs/${jobId}/restart`);
            toast.success("Sweep re-queued — restarting from campaign 1");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed");
        }
    };

    const [refreshing, setRefreshing] = useState(false);
    const [lastRefresh, setLastRefresh] = useState(null);

    const loadLastRefresh = async () => {
        try {
            const { data } = await api.get("/detailed-reports/meta/last-refresh");
            setLastRefresh(data);
        } catch (e) { void e; }
    };
    useEffect(() => {
        loadLastRefresh();
        const id = setInterval(loadLastRefresh, 60000); // poll every minute
        return () => clearInterval(id);
    }, []);

    const refreshTodayNow = async () => {
        setRefreshing(true);
        try {
            const { data } = await api.post("/detailed-reports/meta/refresh-today");
            toast.success(data.message || "Refresh complete");
            await loadLastRefresh();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Refresh failed");
        } finally {
            setRefreshing(false);
        }
    };

    // Load campaigns when ad account changes — but DON'T auto-load report rows
    useEffect(() => {
        if (!filter.ad_account_id) {
            setCampaigns([]);
            return;
        }
        api
            .get("/campaigns", {
                params: { ad_account_id: filter.ad_account_id, has_metrics: "false" },
            })
            .then((r) => setCampaigns(r.data));
        // Clear existing rows so user explicitly sees they need to click Generate again
        setRows([]);
        setHasGenerated(false);
    }, [filter.ad_account_id]);

    // Clear rows whenever any filter changes (forces user to click Generate)
    useEffect(() => {
        setRows([]);
        setHasGenerated(false);
    }, [filter.breakdown, filter.date_from, filter.date_to, filter.campaign_external_id]);

    const loadRows = async () => {
        if (!filter.ad_account_id) return;
        const params = {
            breakdown: filter.breakdown,
            ad_account_id: filter.ad_account_id,
            date_from: filter.date_from,
            date_to: filter.date_to,
        };
        const camp = campaigns.find((c) => c.id === filter.campaign_external_id);
        if (camp?.external_campaign_id) params.campaign_external_id = camp.external_campaign_id;
        const { data } = await api.get("/detailed-reports/meta", { params });
        setRows(data);
        setHasGenerated(true);
    };

    const generate = async () => {
        if (!filter.ad_account_id) return toast.error("Select an ad account");
        setGenerating(true);
        try {
            await loadRows();
            toast.success("Report loaded");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to load report");
        } finally {
            setGenerating(false);
        }
    };

    const pager = usePagination(rows, 50);

    // Totals across the FULL filtered dataset (not just the current page)
    const totals = useMemo(() => {
        const t = { _count: rows.length };
        const moneyCurrencies = new Set();
        let aggImpr = 0, aggClicks = 0, aggLink = 0, aggReach = 0;
        for (const r of rows) {
            for (const m of visibleMetrics) {
                if (NON_SUMMABLE.has(m.key)) continue; // derived/text — computed below
                t[m.key] = (t[m.key] || 0) + (Number(r[m.key]) || 0);
            }
            aggImpr += Number(r.impressions) || 0;
            aggClicks += Number(r.clicks) || 0;
            aggLink += Number(r.link_clicks) || 0;
            aggReach += Number(r.reach) || 0;
            if (r.currency) moneyCurrencies.add(r.currency);
        }
        // Derive margin_pct from totals so it stays mathematically correct
        if (t.display_spend && t.display_spend > 0) {
            t.margin_pct = ((t.margin || 0) / t.display_spend) * 100;
        } else {
            t.margin_pct = 0;
        }
        // Client CPM doesn't aggregate meaningfully — leave blank in totals
        t.client_cpm = null;
        // Ratio metrics computed from dataset-wide raw sums (not naive averages)
        t.frequency = aggReach > 0 ? aggImpr / aggReach : 0;
        t.ctr_all = aggImpr > 0 ? (aggClicks / aggImpr) * 100 : 0;
        t.ctr_link = aggImpr > 0 ? (aggLink / aggImpr) * 100 : 0;
        // Text columns have no total
        t.currency = null;
        t.reporting_start = null;
        t.reporting_end = null;
        // If multiple currencies in the dataset, money totals are ambiguous — flag it
        t._currency = moneyCurrencies.size === 1 ? Array.from(moneyCurrencies)[0] : null;
        return t;
    }, [rows, visibleMetrics]);

    const downloadVisible = () => {
        if (!rows.length) return;
        const base = [
            { key: "metric_date", label: "Date" },
            { key: "campaign_name", label: "Campaign" },
            { key: "external_campaign_id", label: "Campaign ID" },
            { key: "adset_name", label: "Ad set" },
            { key: "ad_name", label: "Ad" },
            { key: "objective", label: "Objective" },
            { key: "currency", label: "Currency" },
        ];
        if (filter.breakdown === "placement") {
            base.push({ key: "breakdown_key1", label: "Publisher" }, { key: "breakdown_key2", label: "Position" });
        } else if (filter.breakdown !== "creative") {
            const label = filter.breakdown.charAt(0).toUpperCase() + filter.breakdown.slice(1);
            base.push({ key: "breakdown_key1", label });
        }
        visibleMetrics.forEach((m) => base.push({ key: m.key, label: m.label }));
        downloadCsv(`meta_${filter.breakdown}_${today}.csv`, base, rows);
        toast.success(`Downloaded ${rows.length} rows · ${visibleMetrics.length} metrics`);
    };

    const showBreakdownCol = filter.breakdown !== "creative";

    return (
        <div data-testid="detailed-reports-meta-page">
            <Link to="/detailed-reports" className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:wl-accent-text mb-3">
                <ChevronLeft className="h-3 w-3" />Back to platforms
            </Link>
            <PageHeader
                eyebrow="Meta Ads · Detailed reports"
                title="Meta detailed insights"
                subtitle="Select an ad account, optional campaign, breakdown, and date range. Click Generate report to load the synced data from the database (refreshed hourly + full historical sweep per connection)."
                actions={
                    <div className="flex items-center gap-2">
                        {!isAdvertiser && lastRefresh?.last_refresh_at && (() => {
                            const last = parseUtc(lastRefresh.last_refresh_at);
                            const minsAgo = Math.round((Date.now() - last.getTime()) / 60000);
                            const ago = minsAgo < 1 ? "just now"
                                : minsAgo < 60 ? `${minsAgo} min ago`
                                : minsAgo < 1440 ? `${Math.round(minsAgo / 60)} h ago`
                                : `${Math.round(minsAgo / 1440)} d ago`;
                            const todayRows = lastRefresh.today?.rows || 0;
                            const yesterdayRows = lastRefresh.yesterday?.rows || 0;
                            return (
                                <div className="text-[11px] text-zinc-500 leading-tight text-right mr-1" data-testid="last-refresh-info" title={last.toLocaleString()}>
                                    <div>Today+yesterday refreshed <span className="text-zinc-800 font-medium">{ago}</span></div>
                                    <div className="text-zinc-400">Today: {todayRows.toLocaleString("en-US")} rows · Yesterday: {yesterdayRows.toLocaleString("en-US")} rows</div>
                                </div>
                            );
                        })()}
                        <button
                            onClick={refreshTodayNow}
                            disabled={refreshing}
                            className={`inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium border border-zinc-300 hover:bg-zinc-100 disabled:opacity-50 ${isAdvertiser ? "hidden" : ""}`}
                            data-testid="meta-detailed-refresh-today-btn"
                            title="Trigger the hourly tick on-demand — refresh today + yesterday for all active campaigns"
                        >
                            {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                            {refreshing ? "Refreshing…" : "Refresh today+yesterday"}
                        </button>
                        <button
                            onClick={startManualSweep}
                            disabled={!selectedConn || !!activeSweep}
                            className={`inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium border border-zinc-300 hover:bg-zinc-100 disabled:opacity-50 ${isAdvertiser ? "hidden" : ""}`}
                            data-testid="meta-detailed-sweep-btn"
                            title="Sweep every campaign × every breakdown starting from each campaign's start date"
                        >
                            <History className="h-4 w-4" />
                            {activeSweep ? "Sweep running…" : "Full historical sweep"}
                        </button>
                        {!isAdvertiser && sweepSucceeded && lastSweep?.finished_at && (() => {
                            const finished = parseUtc(lastSweep.finished_at);
                            const minsAgo = Math.round((Date.now() - finished.getTime()) / 60000);
                            const ago = minsAgo < 1 ? "just now"
                                : minsAgo < 60 ? `${minsAgo} min ago`
                                : minsAgo < 1440 ? `${Math.round(minsAgo / 60)} h ago`
                                : `${Math.round(minsAgo / 1440)} d ago`;
                            return (
                                <div className="text-[11px] text-zinc-500 leading-tight ml-1 flex items-center gap-1.5" data-testid="last-sweep-info" title={finished.toLocaleString()}>
                                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                                    <span>Last sweep <span className="text-zinc-800 font-medium">{ago}</span> · {(lastSweep.rows_upserted || 0).toLocaleString("en-US")} rows</span>
                                </div>
                            );
                        })()}
                    </div>
                }
            />

            {/* Active sweep progress card */}
            {!isAdvertiser && showSweepCard && (() => {
                const j = activeSweep || lastSweep;
                const running = j.status === "queued" || j.status === "running";
                const failed = j.status === "failed";
                const cancelled = j.status === "cancelled";
                // Stall detection: heartbeat older than 5 minutes
                let stalled = false;
                if (j.status === "running" && j.last_heartbeat_at) {
                    const ageMs = Date.now() - new Date(j.last_heartbeat_at).getTime();
                    stalled = ageMs > 5 * 60 * 1000;
                }
                let icon = <Loader2 className="h-5 w-5 wl-accent-text animate-spin" />;
                let title = "Sync in progress";
                if (failed) { icon = <AlertTriangle className="h-5 w-5 text-red-500" />; title = "Sync failed"; }
                else if (cancelled) { icon = <XIcon className="h-5 w-5 text-zinc-500" />; title = "Sweep cancelled"; }
                else if (j.status === "success") { icon = <CheckCircle2 className="h-5 w-5 text-emerald-500" />; title = "Last sweep complete"; }
                else if (stalled) { icon = <Clock className="h-5 w-5 text-amber-500" />; title = "Sync stalled"; }
                return (
                    <div className="bg-white border border-zinc-200 p-5 mb-5" data-testid="sweep-progress-card">
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                                {icon}
                                <div>
                                    <div className="overline">{j.kind === "initial" ? "Initial sweep" : "Manual sweep"}</div>
                                    <h3 className="font-display text-base text-zinc-900">{title}</h3>
                                    {stalled && (
                                        <div className="text-[11px] text-amber-500 mt-0.5">No heartbeat for 5+ min — will auto-recover within 10 sec.</div>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <div className="grid grid-cols-3 gap-4 text-right text-xs">
                                    <div>
                                        <div className="overline">Campaigns</div>
                                        <div className="font-display text-lg tabular-nums">{j.campaigns_done}<span className="text-zinc-400 text-sm"> / {j.campaigns_total}</span></div>
                                    </div>
                                    <div>
                                        <div className="overline">Breakdowns</div>
                                        <div className="font-display text-lg tabular-nums">{j.breakdowns_done}<span className="text-zinc-400 text-sm"> / {j.breakdowns_total || j.campaigns_total * 5}</span></div>
                                    </div>
                                    <div>
                                        <div className="overline">Rows synced</div>
                                        <div className="font-display text-lg tabular-nums wl-accent-text">{(j.rows_upserted || 0).toLocaleString("en-US")}</div>
                                    </div>
                                </div>
                                <div className="flex flex-col gap-1.5">
                                    {running && (
                                        <button onClick={() => cancelSweep(j.id)} className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] border border-zinc-300 hover:bg-zinc-100 text-zinc-700" data-testid="sweep-cancel-btn" title="Stop this sweep">
                                            <XIcon className="h-3 w-3" />Cancel
                                        </button>
                                    )}
                                    {!running && (
                                        <button onClick={() => restartSweep(j.id)} className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] border border-zinc-300 hover:bg-zinc-100 text-zinc-700" data-testid="sweep-restart-btn" title="Restart this sweep from campaign 1">
                                            <RotateCw className="h-3 w-3" />Restart
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                            <div className="h-full transition-all duration-500" style={{ width: `${j.progress_pct || 0}%`, background: failed ? "#ef4444" : cancelled ? "#a1a1aa" : stalled ? "#f59e0b" : "var(--wl-primary-hex)" }} />
                        </div>
                        <div className="flex justify-between mt-2 text-[11px] text-zinc-500">
                            <span data-testid="sweep-current-step">
                                {j.current_step || ""}
                                {j.last_heartbeat_at && running && (
                                    <span className="text-zinc-400 ml-2">· heartbeat {Math.max(0, Math.round((Date.now() - new Date(j.last_heartbeat_at).getTime()) / 1000))}s ago</span>
                                )}
                            </span>
                            <span className="tabular-nums">{j.progress_pct || 0}%</span>
                        </div>
                        {failed && j.error && (
                            <div className="mt-2 text-[11px] text-red-500 bg-red-500/5 border border-red-500/20 px-2 py-1 rounded font-mono">{j.error}</div>
                        )}
                    </div>
                );
            })()}

            <div className="bg-white border border-zinc-200 p-4 mb-5">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                        <label className="overline">Ad account</label>
                        <select value={filter.ad_account_id} onChange={(e) => setFilter({ ...filter, ad_account_id: e.target.value, campaign_external_id: "" })} className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded text-sm" data-testid="meta-detailed-account">
                            <option value="">— pick —</option>
                            {adAccounts.map((a) => (<option key={a.id} value={a.id}>{a.name}</option>))}
                        </select>
                    </div>
                    <div>
                        <label className="overline">Campaign (optional)</label>
                        <select value={filter.campaign_external_id} onChange={(e) => setFilter({ ...filter, campaign_external_id: e.target.value })} className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded text-sm" data-testid="meta-detailed-campaign">
                            <option value="">All campaigns</option>
                            {campaigns.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
                        </select>
                    </div>
                    <div>
                        <label className="overline">Breakdown</label>
                        <select value={filter.breakdown} onChange={(e) => setFilter({ ...filter, breakdown: e.target.value })} className="w-full mt-1 px-3 py-2 bg-zinc-100 border border-zinc-200 rounded text-sm" data-testid="meta-detailed-breakdown">
                            {BREAKDOWNS.map((b) => (<option key={b.value} value={b.value}>{b.label}</option>))}
                        </select>
                    </div>
                    <div>
                        <label className="overline">Date range</label>
                        <div className="flex items-center gap-2 mt-1">
                            <input type="date" value={filter.date_from} onChange={(e) => setFilter({ ...filter, date_from: e.target.value })} className="flex-1 min-w-0 px-2 py-2 bg-zinc-100 border border-zinc-200 rounded text-sm" />
                            <span className="text-zinc-400 text-xs">→</span>
                            <input type="date" value={filter.date_to} onChange={(e) => setFilter({ ...filter, date_to: e.target.value })} className="flex-1 min-w-0 px-2 py-2 bg-zinc-100 border border-zinc-200 rounded text-sm" />
                        </div>
                    </div>
                </div>

                {/* Action bar */}
                <div className="flex flex-wrap items-center justify-between gap-3 mt-4 pt-4 border-t border-zinc-100">
                    <div className="text-xs text-zinc-500" data-testid="generate-hint">
                        {!hasGenerated && rows.length === 0 && (
                            <span>Configure filters above, then click <span className="wl-accent-text font-medium">Generate report</span> to load saved data from the database.</span>
                        )}
                        {hasGenerated && (
                            <span className="tabular-nums">{rows.length.toLocaleString("en-US")} rows · {filter.breakdown} breakdown</span>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        {/* Metrics selector */}
                        <div className="relative">
                            <button
                                type="button"
                                onClick={() => setMetricMenuOpen((v) => !v)}
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded text-sm font-medium border border-zinc-300 hover:bg-zinc-100 text-zinc-700"
                                data-testid="meta-detailed-metrics-btn"
                                title="Choose which metric columns to display"
                            >
                                <Columns3 className="h-4 w-4" />
                                Metrics ({metrics.length}/{allowedMetrics.length})
                            </button>
                            {metricMenuOpen && (
                                <>
                                    <div className="fixed inset-0 z-30" onClick={() => setMetricMenuOpen(false)} />
                                    <div className="absolute right-0 mt-1 w-72 bg-white border border-zinc-200 rounded-md shadow-lg z-40" data-testid="metrics-menu">
                                        <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-200">
                                            <span className="overline">Metrics</span>
                                            <div className="flex items-center gap-2 text-[11px]">
                                                <button type="button" onClick={selectAll} className="wl-accent-text hover:underline" data-testid="metrics-select-all">Select all</button>
                                                <span className="text-zinc-300">·</span>
                                                <button type="button" onClick={clearAll} className="text-zinc-500 hover:text-zinc-900" data-testid="metrics-clear">Clear</button>
                                            </div>
                                        </div>
                                        <div className="max-h-72 overflow-y-auto py-1">
                                            {allowedMetrics.map((m) => {
                                                const checked = metrics.includes(m.key);
                                                return (
                                                    <label key={m.key} className="flex items-center gap-2 px-3 py-1.5 hover:bg-zinc-50 cursor-pointer text-sm" data-testid={`metric-toggle-${m.key}`}>
                                                        <span className={`h-4 w-4 rounded border flex items-center justify-center ${checked ? "wl-accent-bg wl-accent-border" : "border-zinc-300 bg-white"}`}>
                                                            {checked && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                                                        </span>
                                                        <input type="checkbox" checked={checked} onChange={() => toggleMetric(m.key)} className="sr-only" />
                                                        <span className="text-zinc-700">{m.label}</span>
                                                    </label>
                                                );
                                            })}
                                            {allowedMetrics.length === 0 && (
                                                <div className="px-3 py-3 text-[11px] text-zinc-500">
                                                    No metrics enabled for your agency. Contact your super admin.
                                                </div>
                                            )}
                                        </div>
                                        <div className="px-3 py-2 border-t border-zinc-200 text-[10px] text-zinc-500">
                                            {isAdvertiser
                                                ? "Your selection is saved locally and applies to both the table and CSV download."
                                                : allowedMetrics.length < ALL_METRICS.length
                                                    ? `${ALL_METRICS.length - allowedMetrics.length} metric${ALL_METRICS.length - allowedMetrics.length === 1 ? "" : "s"} hidden by super admin · ${allowedMetrics.length} available`
                                                    : "Your selection is saved locally and applies to both the table and CSV download."}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                        <button
                            onClick={downloadVisible}
                            disabled={!rows.length}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded text-sm font-medium border border-zinc-300 hover:bg-zinc-100 disabled:opacity-50 disabled:cursor-not-allowed text-zinc-700"
                            data-testid="meta-detailed-download"
                            title={rows.length ? "Download visible rows as CSV" : "Generate a report first"}
                        >
                            <FileDown className="h-4 w-4" />Download CSV
                        </button>
                        <button
                            onClick={generate}
                            disabled={generating || !filter.ad_account_id}
                            className="inline-flex items-center gap-1.5 px-4 py-2 rounded text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                            style={{ background: "var(--wl-primary-hex)", color: "#fff" }}
                            data-testid="meta-detailed-generate"
                        >
                            {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                            {generating ? "Generating…" : "Generate report"}
                        </button>
                    </div>
                </div>
            </div>

            <div className="bg-white border border-zinc-200 overflow-x-auto">
                <table className="w-full text-xs">
                    <thead>
                        <tr className="bg-zinc-100 border-b border-zinc-200">
                            <th className="overline text-left p-3 whitespace-nowrap">Date</th>
                            <th className="overline text-left p-3 whitespace-nowrap">Campaign ID</th>
                            <th className="overline text-left p-3 whitespace-nowrap">Campaign Name</th>
                            <th className="overline text-left p-3 whitespace-nowrap">Ad Set ID</th>
                            <th className="overline text-left p-3 whitespace-nowrap">Ad Set Name</th>
                            <th className="overline text-left p-3 whitespace-nowrap">Ad ID</th>
                            <th className="overline text-left p-3 whitespace-nowrap">Ad Name</th>
                            <th className="overline text-left p-3 whitespace-nowrap">Objective</th>
                            {showBreakdownCol && filter.breakdown === "placement" && (
                                <>
                                    <th className="overline text-left p-3">Publisher</th>
                                    <th className="overline text-left p-3">Position</th>
                                </>
                            )}
                            {showBreakdownCol && filter.breakdown !== "placement" && (
                                <th className="overline text-left p-3 capitalize">{filter.breakdown}</th>
                            )}
                            {visibleMetrics.map((m) => (
                                <th key={m.key} className="overline text-right p-3 whitespace-nowrap">{m.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {pager.slice.map((r) => (
                            <tr key={r.id} className="border-b border-zinc-200 last:border-0">
                                <td className="p-3 tabular-nums text-zinc-700 whitespace-nowrap">{r.metric_date}</td>
                                <td className="p-3 tabular-nums text-zinc-500 whitespace-nowrap" title={r.external_campaign_id}>{r.external_campaign_id || "—"}</td>
                                <td className="p-3 text-zinc-900 truncate max-w-[220px]" title={r.campaign_name}>{r.campaign_name || "—"}</td>
                                <td className="p-3 tabular-nums text-zinc-500 whitespace-nowrap" title={r.external_adset_id}>{r.external_adset_id || "—"}</td>
                                <td className="p-3 text-zinc-700 truncate max-w-[220px]" title={r.adset_name}>{r.adset_name || "—"}</td>
                                <td className="p-3 tabular-nums text-zinc-500 whitespace-nowrap" title={r.external_ad_id}>{r.external_ad_id || "—"}</td>
                                <td className="p-3 text-zinc-700 truncate max-w-[220px]" title={r.ad_name}>{r.ad_name || "—"}</td>
                                <td className="p-3 text-zinc-700 whitespace-nowrap capitalize" title={r.objective}>{r.objective || "—"}</td>
                                {filter.breakdown === "placement" && (
                                    <>
                                        <td className="p-3 text-zinc-700 capitalize">{r.breakdown_key1 || "—"}</td>
                                        <td className="p-3 text-zinc-700 capitalize">{r.breakdown_key2 || "—"}</td>
                                    </>
                                )}
                                {filter.breakdown !== "placement" && filter.breakdown !== "creative" && (
                                    <td className="p-3 text-zinc-700 capitalize">{r.breakdown_key1 || "—"}</td>
                                )}
                                {visibleMetrics.map((m) => (
                                    <td key={m.key} className="p-3 text-right tabular-nums">
                                        {renderMetric(r, m)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                        {!rows.length && (
                            generating ? <LoadingRow colSpan={20} message="Loading report from database…" /> :
                            !hasGenerated ? (
                                <tr><td colSpan={20} className="p-16 text-center">
                                    <div className="inline-flex flex-col items-center gap-3 text-zinc-500">
                                        <Play className="h-10 w-10 wl-accent-text opacity-50" />
                                        <div className="font-display text-base text-zinc-700">Ready to generate a report</div>
                                        <div className="text-xs max-w-md leading-relaxed">
                                            Pick your ad account, breakdown, and date range above, then click{" "}
                                            <span className="wl-accent-text font-medium">Generate report</span> to load the synced data from the database.
                                        </div>
                                        <div className="text-[11px] text-zinc-400 max-w-md leading-relaxed mt-1">
                                            Data is refreshed automatically every hour for today + yesterday, and a full historical sweep runs once per connection.
                                        </div>
                                    </div>
                                </td></tr>
                            ) : (
                                <tr><td colSpan={20} className="p-10 text-center text-zinc-500">
                                    No saved data in the database for this filter combination. Try widening the date range or running a manual sweep.
                                </td></tr>
                            )
                        )}
                    </tbody>
                    {rows.length > 0 && visibleMetrics.length > 0 && (
                        <tfoot>
                            <tr className="bg-zinc-100 border-t-2 border-zinc-300 font-semibold sticky bottom-0" data-testid="meta-detailed-totals">
                                <td className="p-3 text-zinc-900 overline">Total ({totals._count.toLocaleString("en-US")} rows)</td>
                                <td className="p-3" />
                                <td className="p-3" />
                                <td className="p-3" />
                                <td className="p-3" />
                                <td className="p-3" />
                                <td className="p-3" />
                                <td className="p-3" />
                                {filter.breakdown === "placement" && (
                                    <>
                                        <td className="p-3" />
                                        <td className="p-3" />
                                    </>
                                )}
                                {filter.breakdown !== "placement" && filter.breakdown !== "creative" && (
                                    <td className="p-3" />
                                )}
                                {visibleMetrics.map((m) => {
                                    let content;
                                    if (m.key === "client_cpm") {
                                        // CPM doesn't aggregate meaningfully across rows
                                        content = <span className="text-zinc-400">—</span>;
                                    } else if (m.format === "text") {
                                        content = <span className="text-zinc-400">—</span>;
                                    } else if (m.format === "decimal") {
                                        content = (Number(totals[m.key]) || 0).toFixed(2);
                                    } else if (m.format === "percent") {
                                        content = fmtPct(totals[m.key]);
                                    } else if (m.format === "money") {
                                        content = totals._currency
                                            ? fmtMoney(totals[m.key], totals._currency)
                                            : <span title="Multiple currencies in result — raw sum">{(totals[m.key] || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>;
                                    } else {
                                        content = fmtNum(totals[m.key]);
                                    }
                                    return (
                                        <td key={m.key} className="p-3 text-right tabular-nums text-zinc-900" data-testid={`total-${m.key}`}>
                                            {content}
                                        </td>
                                    );
                                })}
                            </tr>
                        </tfoot>
                    )}
                </table>
                {rows.length > 0 && (
                    <Pagination
                        page={pager.page}
                        totalPages={pager.totalPages}
                        pageSize={pager.pageSize}
                        range={pager.range}
                        setPage={pager.setPage}
                        setPageSize={pager.setPageSize}
                        dataTestId="meta-detailed-pagination"
                    />
                )}
            </div>
        </div>
    );
}
